import { getSessionForAuthenticatedRequest } from '../auth.ts';
import { resolveRuntimeConfig } from '../../config/runtime';
import { getErrorMessage, isAbortLikeError, isTransientFetchError, isTransientHttpStatus, sleep } from '../requestAbort.ts';

export type TenantBootstrapResult = {
  ok: true;
  tenantId: string;
  tenantSlug: string;
  role: string;
  membershipCreated: boolean;
  /** True when the authenticated user is the platform administrator
   * (admin@trade-proof.com) granted cross-tenant access without a membership. */
  superAdmin?: boolean;
};

type TenantBootstrapErrorPayload = {
  error?: string;
  detail?: string;
};

export type TenantBootstrapError = Error & {
  code?: string;
  status?: number;
  timings?: {
    sessionLookupMs: number;
    bootstrapDurationMs: number;
    totalMs: number;
  };
};

type TenantBootstrapOptions = {
  accessToken?: string;
  hostname?: string;
  tenantSlug?: string;
  signal?: AbortSignal;
  correlationId?: string;
};

// Bounded automatic retry for TRANSIENT bootstrap failures (network drop, request
// timeout, 5xx, 429) so a momentary blip during login never surfaces the fatal
// workspace screen. Deterministic 4xx (401/403/404) and aborts are not retried.
const BOOTSTRAP_MAX_ATTEMPTS = 3; // 1 initial + 2 internal retries
const BOOTSTRAP_RETRY_BASE_DELAY_MS = 300;

/**
 * POSTs the tenant-bootstrap request with automatic, bounded retry for transient
 * failures only. The server handler upserts idempotently, so a retry after a
 * partially-applied request is safe.
 */
async function fetchTenantBootstrapWithRetry({ accessToken, hostname, tenantSlug, signal, correlationId }: {
  accessToken: string;
  hostname: string;
  tenantSlug: string;
  signal?: AbortSignal;
  correlationId?: string;
}): Promise<Response> {
  const startedAt = readNow();
  for (let attempt = 1; ; attempt += 1) {
    try {
      const response = await fetch('/api/tenant/bootstrap', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...(correlationId ? { 'x-tp-bootstrap-id': correlationId } : {}),
        },
        body: JSON.stringify({ hostname, tenantSlug }),
        signal,
      });

      if (response.ok || !isTransientHttpStatus(response.status) || attempt >= BOOTSTRAP_MAX_ATTEMPTS) {
        tpStripeFlashLog('client_tenant_bootstrap_finish', {
          correlationId: correlationId || null,
          attempt,
          retryOccurred: attempt > 1,
          durationMs: roundDurationMs(readNow() - startedAt),
          success: response.ok,
        });
        return response;
      }

      // Transient 5xx / 429: release the response body before retrying.
      try { await response.body?.cancel?.(); } catch {}
      tpStripeFlashLog('tenant_bootstrap_retry', {
        attempt,
        status: response.status,
      });
      await sleep(BOOTSTRAP_RETRY_BASE_DELAY_MS * attempt);
    } catch (error) {
      if (attempt >= BOOTSTRAP_MAX_ATTEMPTS || !isTransientFetchError(error)) {
        tpStripeFlashLog('client_tenant_bootstrap_finish', {
          correlationId: correlationId || null,
          attempt,
          retryOccurred: attempt > 1,
          durationMs: roundDurationMs(readNow() - startedAt),
          success: false,
          failureCategory: isAbortLikeError(error) ? 'aborted' : 'request_error',
        });
        throw error;
      }
      tpStripeFlashLog('tenant_bootstrap_retry', {
        attempt,
        error: getErrorMessage(error),
      });
      await sleep(BOOTSTRAP_RETRY_BASE_DELAY_MS * attempt);
    }
  }
}

const readNow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const roundDurationMs = (value: number) => Number(value.toFixed(1));
const createTenantBootstrapError = (
  message: string,
  code: string,
  status: number | null,
  timings: {
    sessionLookupMs: number;
    bootstrapDurationMs: number;
    totalMs: number;
  },
) => {
  const error = new Error(message) as TenantBootstrapError;
  error.code = code;
  if (status != null) {
    error.status = status;
  }
  error.timings = timings;
  return error;
};

const isExpiredJwtMessage = (message: string) => {
  const normalized = String(message || '').trim().toLowerCase();
  return normalized.includes('token is expired')
    || normalized.includes('invalid jwt')
    || normalized.includes('unable to parse or verify signature');
};

const tpStripeFlashLog = (event: string, payload: Record<string, unknown> = {}) => {
  try {
    const monoTs = typeof performance !== 'undefined' ? performance.now() : null;
    console.info('[TP-STRIPE-FLASH]', event, { ts: Date.now(), monoTs, ...payload });
  } catch {}
};

export async function bootstrapTenantMembership(options?: TenantBootstrapOptions): Promise<TenantBootstrapResult> {
  const startedAt = readNow();
  let sessionLookupMs = 0;
  let accessToken = String(options?.accessToken || '').trim();

  if (options?.signal?.aborted) {
    throw createTenantBootstrapError(
      'Tenant bootstrap request was interrupted.',
      'tenant_bootstrap_aborted',
      null,
      { sessionLookupMs: 0, bootstrapDurationMs: 0, totalMs: 0 },
    );
  }

  tpStripeFlashLog('tenant_bootstrap_helper_enter', {
    hostname: String(options?.hostname || (typeof window !== 'undefined' ? window.location.hostname : '')).trim() || null,
    tenantSlug: options?.tenantSlug || null,
    hasAccessTokenOverride: !!accessToken,
    correlationId: options?.correlationId || null,
  });

  if (!accessToken) {
    const sessionLookupStartedAt = readNow();
    try {
      const session = await getSessionForAuthenticatedRequest({ reason: 'tenant_bootstrap' });
      sessionLookupMs = readNow() - sessionLookupStartedAt;
      accessToken = String(session?.access_token || '').trim();
      if (options?.signal?.aborted) {
        throw createTenantBootstrapError(
          'Tenant bootstrap request was interrupted.',
          'tenant_bootstrap_aborted',
          null,
          {
            sessionLookupMs: roundDurationMs(sessionLookupMs),
            bootstrapDurationMs: 0,
            totalMs: roundDurationMs(readNow() - startedAt),
          },
        );
      }
    } catch (error) {
      sessionLookupMs = readNow() - sessionLookupStartedAt;
      if (isAbortLikeError(error)) {
        const totalMs = readNow() - startedAt;
        throw createTenantBootstrapError(
          'Tenant bootstrap request was interrupted.',
          'tenant_bootstrap_aborted',
          null,
          {
            sessionLookupMs: roundDurationMs(sessionLookupMs),
            bootstrapDurationMs: 0,
            totalMs: roundDurationMs(totalMs),
          },
        );
      }
      const message = String((error as Error)?.message || 'No authenticated session is available for tenant bootstrap.').trim()
        || 'No authenticated session is available for tenant bootstrap.';
      const code = String((error as { code?: string } | undefined)?.code || '').trim() || 'auth_session_missing';
      const status = (error as { status?: number } | undefined)?.status ?? null;
      const totalMs = readNow() - startedAt;
      const tenantBootstrapError = createTenantBootstrapError(
        message,
        code,
        status,
        {
          sessionLookupMs: roundDurationMs(sessionLookupMs),
          bootstrapDurationMs: 0,
          totalMs: roundDurationMs(totalMs),
        },
      );
      tpStripeFlashLog('tenant_bootstrap_helper_exit', {
        ok: false,
        tenantSlug: String(options?.tenantSlug || '') || null,
        error: message,
        code,
        status,
        sessionLookupMs: roundDurationMs(sessionLookupMs),
        bootstrapDurationMs: 0,
        totalMs: roundDurationMs(totalMs),
      });
      throw tenantBootstrapError;
    }
  }

  if (!accessToken) {
    throw new Error('No authenticated session is available for tenant bootstrap.');
  }

  const hostname = String(options?.hostname || (typeof window !== 'undefined' ? window.location.hostname : '')).trim();
  const runtime = resolveRuntimeConfig({ hostname });
  if (runtime.source === 'default' && hostname) {
    throw createTenantBootstrapError(
      `Unsupported hostname for tenant bootstrap: ${hostname}.`,
      'tenant_host_unsupported',
      null,
      {
        sessionLookupMs: roundDurationMs(sessionLookupMs),
        bootstrapDurationMs: 0,
        totalMs: roundDurationMs(readNow() - startedAt),
      },
    );
  }
  // When the tenant is resolved from the hostname, the server derives the slug from
  // the hostname authoritatively. Only send an explicit slug when the caller provided
  // one or when there is no hostname to resolve (env/default flows).
  const tenantSlug = String(options?.tenantSlug || (runtime.source === 'hostname' ? '' : runtime.tenant.id)).trim();
  const requestedHostname = runtime.source === 'hostname' ? hostname : '';

  const requestStartedAt = readNow();
  let response: Response;
  try {
    tpStripeFlashLog('client_tenant_bootstrap_start', {
      correlationId: options?.correlationId || null,
    });
    response = await fetchTenantBootstrapWithRetry({
      accessToken,
      hostname: requestedHostname,
      tenantSlug,
      signal: options?.signal,
      correlationId: options?.correlationId,
    });
  } catch (error) {
    const bootstrapDurationMs = readNow() - requestStartedAt;
    const totalMs = readNow() - startedAt;
    if (isAbortLikeError(error)) {
      throw createTenantBootstrapError(
        'Tenant bootstrap request was interrupted.',
        'tenant_bootstrap_aborted',
        null,
        {
          sessionLookupMs: roundDurationMs(sessionLookupMs),
          bootstrapDurationMs: roundDurationMs(bootstrapDurationMs),
          totalMs: roundDurationMs(totalMs),
        },
      );
    }

    throw createTenantBootstrapError(
      getErrorMessage(error) || 'Tenant bootstrap failed.',
      'tenant_bootstrap_failed',
      null,
      {
        sessionLookupMs: roundDurationMs(sessionLookupMs),
        bootstrapDurationMs: roundDurationMs(bootstrapDurationMs),
        totalMs: roundDurationMs(totalMs),
      },
    );
  }
  const bootstrapDurationMs = readNow() - requestStartedAt;

  const payload = (await response.json().catch(() => ({}))) as TenantBootstrapResult & TenantBootstrapErrorPayload;
  const totalMs = readNow() - startedAt;

  if (!response.ok) {
    tpStripeFlashLog('tenant_bootstrap_helper_response', {
      status: response.status,
      sessionLookupMs: roundDurationMs(sessionLookupMs),
      bootstrapDurationMs: roundDurationMs(bootstrapDurationMs),
      totalMs: roundDurationMs(totalMs),
      ok: false,
      tenantSlug: String(payload?.error || options?.tenantSlug || '') || null,
    });
  }

  if (!response.ok) {
    const message = payload.detail || payload.error || 'Tenant bootstrap failed.';
    const errorCode = response.status === 401
      ? (isExpiredJwtMessage(message) ? 'auth_session_expired' : 'auth_session_invalid')
      : payload.error || 'tenant_bootstrap_failed';
    const error = createTenantBootstrapError(message, errorCode, response.status, {
      sessionLookupMs: roundDurationMs(sessionLookupMs),
      bootstrapDurationMs: roundDurationMs(bootstrapDurationMs),
      totalMs: roundDurationMs(totalMs),
    });
    tpStripeFlashLog('tenant_bootstrap_helper_exit', {
      ok: false,
      tenantSlug: String(options?.tenantSlug || '') || null,
      error: message,
      code: error.code || null,
      status: response.status,
      sessionLookupMs: roundDurationMs(sessionLookupMs),
      bootstrapDurationMs: roundDurationMs(bootstrapDurationMs),
      totalMs: roundDurationMs(totalMs),
    });
    throw error;
  }

  tpStripeFlashLog('tenant_bootstrap_helper_exit', {
    ok: true,
    tenantId: payload.tenantId,
    tenantSlug: payload.tenantSlug,
    role: payload.role,
    membershipCreated: payload.membershipCreated,
    sessionLookupMs: roundDurationMs(sessionLookupMs),
    bootstrapDurationMs: roundDurationMs(bootstrapDurationMs),
    totalMs: roundDurationMs(totalMs),
  });

  return payload;
}
